import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { GraphsDataSource } from './graphs.datasource';
import { LoadProfileRecord, PowerInputProfileRecord, SeriesPoint, SocProfileRecord } from '../models/graphs.types';
import { Period } from '../../../core/constants/periods';

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

function formatTimeToHHMM(time: string | undefined | null): string {
  if (!time) {
    return '';
  }
  // Input: "00:15:00.000Z"
  // Output: "00:15" or "01:30"
  const match = time.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : time;
}

@Injectable({ providedIn: 'root' })
export class GraphsService {
  private dataSource = inject(GraphsDataSource);

  getLoadSeries(projectId: string, period: Period): Observable<SeriesPoint[]> {
    return this.dataSource.getGraphs(projectId, period).pipe(
      map(res =>
        toArray<LoadProfileRecord>(res?.Load_Profile__c).map(r => ({
          x: formatTimeToHHMM(r?.Time__c),
          y: r?.Load__c ?? 0,
        })),
      ),
    );
  }

  getSocSeries(projectId: string, period: Period): Observable<SeriesPoint[]> {
    return this.dataSource.getGraphs(projectId, period).pipe(
      map(res =>
        toArray<SocProfileRecord>(res?.SOC_Profile__c).map(r => ({
          x: formatTimeToHHMM(r?.time),
          y: r?.soc ?? 0,
        })),
      ),
    );
  }

  getPowerInputSeries(projectId: string, period: Period): Observable<SeriesPoint[]> {
    return this.dataSource.getGraphs(projectId, period).pipe(
      map(res =>
        toArray<PowerInputProfileRecord>(res?.Power_Input_Profile__c).map(r => ({
          x: formatTimeToHHMM(r?.time),
          y: r?.power_input ?? 0,
        })),
      ),
    );
  }

  getAllSeries(
    projectId: string,
    period: Period,
  ): Observable<{
    load: SeriesPoint[];
    soc: SeriesPoint[];
    powerInput: SeriesPoint[];
  }> {
    return this.dataSource.getGraphs(projectId, period).pipe(
      map(res => {
        const load = toArray<LoadProfileRecord>(res?.Load_Profile__c);
        const soc = toArray<SocProfileRecord>(res?.SOC_Profile__c);
        const powerInput = toArray<PowerInputProfileRecord>(res?.Power_Input_Profile__c);

        return {
          load: load.map(r => ({ x: formatTimeToHHMM(r?.Time__c), y: r?.Load__c ?? 0 })),
          soc: soc.map(r => ({ x: formatTimeToHHMM(r?.time), y: r?.soc ?? 0 })),
          powerInput: powerInput.map(r => ({ x: formatTimeToHHMM(r?.time), y: r?.power_input ?? 0 })),
        };
      }),
    );
  }
}
